import Image from 'next/legacy/image';
import { FC, useEffect, useState } from 'react';
import ReactTooltip from 'react-tooltip';

import { DsoLineItemProps, Styled } from './';

import { Dropdown } from 'components/core';
import { DEFAULT_COLORS } from 'components/core/colors';
import Link from 'next/link';
import { useI18n } from 'contexts/i18n';
import { IconWrapper, LoadingSpinner } from 'styles/sharedStyles';
import {
  useDsoRemoveLineItemFromCart,
  useDsoUpdateLineItemsToCart,
} from 'reactQuery/mutations/dso/';
import PriceColumn from './PriceColumn';
import { buildDiscountLabel, hasBulkDiscount } from './DSOShoppingCart.helper';
import { DsoCartErrorModal } from 'components/DSO/DsoCartErrorModal';
import { isDsoCartValidationError } from 'components/DSO/DsoCartErrorModal/DsoCartErrorModal.helper';
import { DsoCartValidationError } from 'types/dso/DsoCart.types';

export const DSOLineItem: FC<DsoLineItemProps> = ({
  lineItem,
  setCheckoutError,
}) => {
  const dsoRemoveLineItemMutation = useDsoRemoveLineItemFromCart();
  const dsoUpdateQuantityMutation = useDsoUpdateLineItemsToCart();
  const [quantity, setQuantity] = useState(lineItem.quantity.toString());
  const [cartError, setCartError] = useState<DsoCartValidationError | null>(
    null
  );
  const i18n = useI18n();

  const deleteLineItem = (childLineItemId: number) => {
    dsoRemoveLineItemMutation.mutate(childLineItemId);
    setCheckoutError([]);
  };

  const updateLineItemQuantity = (
    childLineItemId: number,
    quantity: number,
    shipAccountNumber: string
  ) => {
    dsoUpdateQuantityMutation.mutate(
      { childLineItemId, quantity, shipAccountNumber },
      {
        onError: (err: any) => {
          setQuantity(lineItem.quantity.toString());
          if (isDsoCartValidationError(err?.response?.data)) {
            setCartError(err.response.data);
          }
        },
        onSuccess: () => {
          setQuantity(quantity.toString());
          setCheckoutError([]);
        },
      }
    );
  };

  const discountPercentage = lineItem.children?.[0]?.discountPercentage;
  const discountAmount = lineItem.children?.[0]?.displayDiscountTotal ?? '';
  const hasDiscount = hasBulkDiscount(discountPercentage);
  const discountLabel = hasDiscount
    ? buildDiscountLabel(discountPercentage ?? '', i18n)
    : '';

  return (
    <>
      <Styled.LineItemContainer disabled={false}>
        <Styled.LineItemThumbnail>
          <Link href={`/dso/product/${lineItem.slug}`} passHref>
            <Image
              src={lineItem.imageUrl}
              alt={lineItem.name}
              layout="fill"
              objectFit="cover"
              placeholder="blur"
              blurDataURL="data:image/svg+xml;base64,Cjxzdmcgd2lkdGg9IjcwMCIgaGVpZ2h0PSI0NzUiIHZlcnNpb249IjEuMSIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIiB4bWxuczp4bGluaz0iaHR0cDovL3d3dy53My5vcmcvMTk5OS94bGluayI+CiAgPGRlZnM+CiAgICA8bGluZWFyR3JhZGllbnQgaWQ9ImciPgogICAgICA8c3RvcCBzdG9wLWNvbG9yPSIjY2JjYmNiIiBvZmZzZXQ9IjIwJSIgLz4KICAgICAgPHN0b3Agc3RvcC1jb2xvcj0iI2Y5ZjlmOSIgb2Zmc2V0PSI1MCUiIC8+CiAgICAgIDxzdG9wIHN0b3AtY29sb3I9IiNjYmNiY2IiIG9mZnNldD0iNzAlIiAvPgogICAgPC9saW5lYXJHcmFkaWVudD4KICA8L2RlZnM+CiAgPHJlY3Qgd2lkdGg9IjcwMCIgaGVpZ2h0PSI0NzUiIGZpbGw9IiNjYmNiY2IiIC8+CiAgPHJlY3QgaWQ9InIiIHdpZHRoPSI3MDAiIGhlaWdodD0iNDc1IiBmaWxsPSJ1cmwoI2cpIiAvPgogIDxhbmltYXRlIHhsaW5rOmhyZWY9IiNyIiBhdHRyaWJ1dGVOYW1lPSJ4IiBmcm9tPSItNzAwIiB0bz0iNzAwIiBkdXI9IjFzIiByZXBlYXRDb3VudD0iaW5kZWZpbml0ZSIgIC8+Cjwvc3ZnPg=="
            />
          </Link>
        </Styled.LineItemThumbnail>
        <Styled.LineItemDescription>
          {lineItem.name}
          <Styled.LineItemAttributes>
            <Styled.DiscountPercentage hasDiscount={hasDiscount}>
              {discountLabel}
            </Styled.DiscountPercentage>
          </Styled.LineItemAttributes>
        </Styled.LineItemDescription>

        <Styled.LineItemQuantity>
          <Dropdown
            options={lineItem.quantityLimit.map(limit => ({
              label: limit.toString(),
              value: limit.toString(),
            }))}
            onChange={val => {
              setQuantity(val);
              updateLineItemQuantity(
                lineItem.children[0].childLineItemId,
                parseInt(val),
                ''
              );
            }}
            selectedValue={quantity}
            label="Qty"
            width={'80px'}
            isDisabled={false}
            isLoading={dsoUpdateQuantityMutation.isLoading}
          ></Dropdown>
        </Styled.LineItemQuantity>

        <Styled.LineItemTotal>{lineItem.displayUnitPrice}</Styled.LineItemTotal>

        <PriceColumn
          subtotal={lineItem.displaySubTotal}
          netTotal={lineItem.displayNetTotal}
          showDiscount={hasDiscount}
        />
        <IconWrapper
          onClick={() => deleteLineItem(lineItem.children[0].childLineItemId)}
        >
          {!dsoRemoveLineItemMutation.isLoading && (
            <Image
              src="/images/icons/delete.svg"
              alt="me"
              width="24"
              height="24"
            />
          )}
          {dsoRemoveLineItemMutation.isLoading && <LoadingSpinner />}
        </IconWrapper>

        <ReactTooltip
          place="bottom"
          className={'tooltip'}
          backgroundColor={DEFAULT_COLORS.primaryHover}
        />
      </Styled.LineItemContainer>

      <DsoCartErrorModal
        isOpen={cartError !== null}
        error={cartError}
        onClose={() => setCartError(null)}
      />
    </>
  );
};
